/**
 * Budget Pay cart core — Live TV COA (Live TV extension: budget-pay-bp-cart-core.js).
 * PDP uses the Budget Pay app copy (bp-cart-core.js). Keep LTV + PDP bp-rules in sync.
 */

import {
  BP_MIN_PROPERTY_KEY,
  BP_PLANS_PROPERTY_KEY,
  DEFAULT_BP_GUEST_LIMIT,
  computeBpExposureAtTargetCount,
  getLineFinancedTotalCents,
  getBudgetPayCartLines,
  getLinePlanCount,
  getLinePlanCountOrMin,
  getMinInstallmentsFromLine,
  getTargetInstallmentCount,
  linesNeedingSync,
  parseInstallmentCountFromPlanName,
  resolvePlanForCartLine,
  validateEligibility,
} from './budget-pay-bp-rules.js';

// =============================================================================
// CONSTANTS
// =============================================================================

export const BP_PLANS_SCRIPT_SELECTOR = 'script[data-budget-pay-plans]';
export const SELLING_PLAN_INPUT_NAME = 'selling_plan';
export const TOGGLE_SELECTOR = '[data-budget-pay-toggle]';
/** Default hidden input id (PDP). Live TV should use a scoped id per form. */
export const DEFAULT_SELLING_PLAN_INPUT_ID = 'budget-pay-selling-plan-input';
export const ERROR_BANNER_CLASS = 'budget-pay-cart-error';
export const CART_UPDATE_SYNC_DEBOUNCE_MS = 150;

/** Enable verbose LTV BP logs in DevTools: localStorage.setItem('ltv_bp_debug', '1') */
export const LTV_BP_DEBUG_KEY = 'ltv_bp_debug';

export function isLtvBpDebugEnabled() {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem(LTV_BP_DEBUG_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} label
 * @param {unknown} [detail]
 */
export function ltvBpDebugLog(label, detail) {
  if (!isLtvBpDebugEnabled()) return;
  if (detail !== undefined) {
    console.log(`[BudgetPay][LTV] ${label}`, detail);
  } else {
    console.log(`[BudgetPay][LTV] ${label}`);
  }
}

export {
  DEFAULT_BP_GUEST_LIMIT,
  parseInstallmentCountFromPlanName,
  getBudgetPayCartLines,
  getMinInstallmentsFromLine,
  getLinePlanCount,
  getTargetInstallmentCount,
};

// =============================================================================
// ELIGIBILITY
// =============================================================================

export function parseBooleanOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return defaultValue;
}

export function parseNumberOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

/**
 * @param {DOMStringMap | Record<string, string>} d
 */
export function getEligibilityFromDataset(d) {
  const ds = d && typeof d === 'object' ? d : {};
  const guestLimit = parseNumberOrDefault(ds.bpGuestLimit, DEFAULT_BP_GUEST_LIMIT);
  const isAuthenticated = parseBooleanOrDefault(ds.bpIsAuthenticated, false);
  const available = parseBooleanOrDefault(ds.bpAvailable, true);
  const creditLimit = parseNumberOrDefault(ds.bpCreditLimit, guestLimit);
  return { isAuthenticated, available, creditLimit, guestLimit };
}

export function computeBpSubtotalCents(cart) {
  const bpLines = getBudgetPayCartLines(cart);
  let total = 0;
  for (const line of bpLines) {
    const financed = getLineFinancedTotalCents(line);
    if (financed > 0) total += financed;
  }
  return { bpLines, subtotalCents: total, ok: true, message: '' };
}

// =============================================================================
// PLAN MAP (DOM)
// =============================================================================

export function mergeBpSellingPlansFromDomIntoBody() {
  let bodyMap = {};
  try {
    const parsed = JSON.parse(document.body.dataset.bpSellingPlans || '{}');
    if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      bodyMap = { ...parsed };
    }
  } catch {
    bodyMap = {};
  }

  document.querySelectorAll(BP_PLANS_SCRIPT_SELECTOR).forEach((el) => {
    let productMap = {};
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        productMap = parsed;
      } else {
        return;
      }
    } catch {
      return;
    }

    for (const [k, v] of Object.entries(productMap)) {
      const existing = bodyMap[k];
      if (existing != null && String(existing) !== String(v)) {
        console.warn(
          '[BudgetPay] Conflicting selling plan id for installment',
          k,
          '- keeping existing value; verify all BP products share the same selling plan groups.',
        );
        continue;
      }
      bodyMap[k] = v;
    }
  });

  try {
    document.body.dataset.bpSellingPlans = JSON.stringify(bodyMap);
  } catch {
    // Ignore
  }
}

/**
 * @param {number|string} installmentCount
 * @param {string} productHandle
 * @param {ParentNode} [root]
 */
export function getSellingPlanGid(installmentCount, productHandle, root = document) {
  const key = String(installmentCount);
  try {
    const bodyMap = JSON.parse(document.body.dataset.bpSellingPlans || '{}');
    if (bodyMap[key] != null) return bodyMap[key];
    if (bodyMap[installmentCount] != null) return bodyMap[installmentCount];
  } catch {
    // Ignore
  }

  const esc = productHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const productDataEl = root.querySelector(`[data-bp-product-plans="${esc}"]`);
  if (productDataEl) {
    try {
      const productMap = JSON.parse(productDataEl.textContent || '{}');
      if (productMap[key] != null) return productMap[key];
      if (productMap[installmentCount] != null) return productMap[installmentCount];
    } catch {
      // Ignore
    }
  }

  return null;
}

/**
 * @param {string} productHandle
 * @param {ParentNode} [root]
 * @returns {Record<string, string | number>}
 */
export function getProductPlansMap(productHandle, root = document) {
  const esc = productHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const productDataEl = root.querySelector(`[data-bp-product-plans="${esc}"]`);
  if (!productDataEl) return {};
  try {
    const productMap = JSON.parse(productDataEl.textContent || '{}');
    if (productMap != null && typeof productMap === 'object' && !Array.isArray(productMap)) {
      return productMap;
    }
  } catch {
    // ignore
  }
  return {};
}

export function resolvePlanForLineItem(item, targetCount, root = document) {
  return resolvePlanForCartLine(item, targetCount, (count, handle) =>
    getSellingPlanGid(count, handle, root),
  );
}

// =============================================================================
// UI ERRORS
// =============================================================================

export function showBudgetPayError(message, toggleEl) {
  document.dispatchEvent(
    new CustomEvent('budget-pay:cart-error', {
      bubbles: true,
      detail: { message },
    }),
  );

  const host = toggleEl?.closest?.('.budget-pay-toggle') ?? toggleEl;
  if (!host?.parentElement) return;

  let banner = host.parentElement.querySelector(`.${ERROR_BANNER_CLASS}`);
  if (!banner) {
    banner = document.createElement('p');
    banner.className = ERROR_BANNER_CLASS;
    banner.setAttribute('role', 'alert');
    banner.style.cssText =
      'color:#b00020;font-size:13px;margin:8px 0 0;padding:8px;border:1px solid #b00020;border-radius:6px;background:#fff5f5;';
    host.insertAdjacentElement('afterend', banner);
  }
  banner.textContent = message;
}

export function clearBudgetPayError(toggleEl) {
  const host = toggleEl?.closest?.('.budget-pay-toggle') ?? toggleEl;
  const banner = host?.parentElement?.querySelector?.(`.${ERROR_BANNER_CLASS}`);
  if (banner) banner.remove();
}

// =============================================================================
// CART AJAX
// =============================================================================

export async function fetchCart() {
  const res = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (res.status === 429) {
    const err = new Error('Too many requests. Please wait a moment and try again.');
    err._ltvTooManyRequests = true;
    throw err;
  }
  if (!res.ok) throw new Error('Could not load cart.');
  return res.json();
}

export function normalizeSellingPlanForAjax(planValue) {
  if (planValue == null) return planValue;
  const s = String(planValue);
  const m = s.match(/SellingPlan\/(\d+)/i);
  if (m) return Number(m[1]);
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? s : n;
}

export function getCartLineNumber(cart, lineItem) {
  const items = cart?.items ?? [];
  const idx = items.findIndex((item) => item?.key != null && item.key === lineItem?.key);
  return idx >= 0 ? idx + 1 : 0;
}

export async function changeCartLine(lineNumber, quantity, sellingPlanValue) {
  const body = {
    line: lineNumber,
    quantity,
    selling_plan: normalizeSellingPlanForAjax(sellingPlanValue),
  };
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 429) {
      const err = new Error('Too many requests. Please wait a moment and try again.');
      err._ltvTooManyRequests = true;
      throw err;
    }
    const msg = data?.description || data?.message || `Cart update failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

/**
 * Bidirectional tier sync for all BP lines in cart.
 *
 * @param {unknown} cart
 * @param {number} targetCount
 * @param {ParentNode} [root]
 */
export async function alignCartBpLinesToTargetCount(cart, targetCount, root = document) {
  let currentCart = cart;
  /* Safety cap: one pass per BP line + 2 spare. Prevents an infinite loop when a line's
     plan name is unparseable and linesNeedingSync never returns empty. Without this cap
     the loop hammers /cart/change.js until Shopify returns 429.
     changeCartLine now returns the full updated cart so we no longer need an extra
     GET /cart.js after every change — one less request per BP line realigned. */
  const initialBpLines = getBudgetPayCartLines(currentCart);
  const maxIterations = Math.min(initialBpLines.length + 2, 20);
  const seenLineKeys = {};

  for (let i = 0; i < maxIterations; i++) {
    const bpLinesNow = getBudgetPayCartLines(currentCart);
    const toSync = linesNeedingSync(bpLinesNow, targetCount, getLinePlanCountOrMin);
    if (toSync.length === 0) break;

    const line = toSync[0];

    /* If this line key appears again it means the change.js response did not move it
       to the target plan — stop rather than loop forever. */
    if (seenLineKeys[line.key]) {
      throw new Error('Budget Pay could not be aligned. Please refresh and try again.');
    }
    seenLineKeys[line.key] = true;

    const gid = resolvePlanForLineItem(line, targetCount, root);
    if (!gid) {
      throw new Error(
        `This product needs ${targetCount} installments, but another item in your cart cannot use that plan. Remove it or use a compatible product.`,
      );
    }
    const lineNumber = getCartLineNumber(currentCart, line);
    if (!lineNumber) {
      throw new Error('Could not find a cart line to update. Refresh and try again.');
    }
    const live = currentCart?.items?.find((item) => item?.key === line?.key);
    const quantity = live?.quantity ?? line.quantity;
    /* Use the cart returned by change.js directly — it is a full cart payload and saves
       an extra GET /cart.js on every iteration. */
    currentCart = await changeCartLine(lineNumber, quantity, gid);
  }
  return currentCart;
}

/** @deprecated Use alignCartBpLinesToTargetCount */
export async function alignCartSubscriptionLinesIfNeeded(cart, targetCount, root = document) {
  return alignCartBpLinesToTargetCount(cart, targetCount, root);
}

/**
 * Recompute tier from remaining BP lines and sync (e.g. after remove or qty 0).
 */
export async function alignCartBpTierFromCart(root = document) {
  mergeBpSellingPlansFromDomIntoBody();
  let cart = await fetchCart();
  const bpLines = getBudgetPayCartLines(cart);
  if (bpLines.length === 0) return cart;
  const targetCount = getTargetInstallmentCount(bpLines, getMinInstallmentsFromLine);
  if (!targetCount) return cart;
  return alignCartBpLinesToTargetCount(cart, targetCount, root);
}

// =============================================================================
// FORM / LINE PROPERTIES
// =============================================================================

/**
 * @param {HTMLFormElement | null} form
 * @param {string} productHandle
 * @param {number} minInstallments
 * @param {ParentNode} [root]
 */
export function applyBpLinePropertiesToForm(form, productHandle, minInstallments, root = document) {
  if (!form) return;
  const plans = getProductPlansMap(productHandle, root);
  const ensure = (name, value) => {
    let input = form.querySelector(`input[name="properties[${name}]"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      form.appendChild(input);
    }
    input.value = value;
  };
  ensure(BP_MIN_PROPERTY_KEY, String(minInstallments));
  ensure(BP_PLANS_PROPERTY_KEY, JSON.stringify(plans));
}

/**
 * @param {HTMLFormElement} form
 * @param {string} productHandle
 * @param {number} targetCount
 * @param {string} inputId
 * @param {ParentNode} [root]
 * @returns {string|number|null}
 */
export function applySellingPlanToForm(form, productHandle, targetCount, inputId, root = document) {
  const gid = getSellingPlanGid(targetCount, productHandle, root);
  if (!gid) {
    ltvBpDebugLog('applySellingPlanToForm: no gid', { productHandle, targetCount });
    throw new Error('No selling plan is available for this installment count.');
  }

  const minInstallments = parseInt(
    form?.closest('[data-budget-pay-toggle]')?.dataset?.minInstallments || String(targetCount),
    10,
  );
  applyBpLinePropertiesToForm(
    form,
    productHandle,
    Number.isFinite(minInstallments) && minInstallments > 0 ? minInstallments : targetCount,
    root,
  );

  let input = null;
  let inputSource = 'none';
  if (form && inputId) {
    const byId = document.getElementById(inputId);
    if (byId && form.contains(byId)) {
      input = byId;
      inputSource = 'byId-in-form';
    } else if (byId) {
      ltvBpDebugLog('applySellingPlanToForm: selling_plan id exists but not inside form', {
        inputId,
        formId: form.id,
        formAction: form.getAttribute('action'),
      });
    }
  }
  if (!input && form) {
    input = form.querySelector(`input[name="${SELLING_PLAN_INPUT_NAME}"]`);
    if (input) inputSource = 'name-in-form';
  }
  if (!input && inputId === DEFAULT_SELLING_PLAN_INPUT_ID) {
    input = document.getElementById(inputId);
    if (input) inputSource = 'default-id-global';
  }
  if (!input) {
    if (!form) {
      ltvBpDebugLog('applySellingPlanToForm: no form element', { inputId, productHandle, targetCount });
      throw new Error('Could not find product form.');
    }
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = SELLING_PLAN_INPUT_NAME;
    input.id = inputId;
    form.appendChild(input);
    inputSource = 'created';
  }
  input.value = String(gid);
  ltvBpDebugLog('applySellingPlanToForm: ok', {
    inputSource,
    inputId,
    gid,
    formId: form?.id,
    inForm: form ? form.contains(input) : false,
  });
  return gid;
}

export function setProductFormSubmitDisabledForForm(form, disabled) {
  if (!form) return;
  form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((btn) => {
    if (disabled) {
      if (!btn.hasAttribute('data-bp-was-disabled')) {
        btn.setAttribute('data-bp-was-disabled', btn.disabled ? 'true' : 'false');
      }
      btn.disabled = true;
    } else if (btn.hasAttribute('data-bp-was-disabled')) {
      const was = btn.getAttribute('data-bp-was-disabled') === 'true';
      btn.disabled = was;
      btn.removeAttribute('data-bp-was-disabled');
    }
  });
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} inputId
 */
export function removeSellingPlanInputFromForm(form, inputId) {
  if (form && inputId) {
    const byId = document.getElementById(inputId);
    if (byId && form.contains(byId)) {
      byId.remove();
      return;
    }
  }
  const legacy = document.getElementById(inputId);
  if (legacy) legacy.remove();
}

// =============================================================================
// SYNC CONTEXT
// =============================================================================

/**
 * @typedef {object} BudgetPaySyncContext
 * @property {HTMLElement} toggleEl
 * @property {() => string} getProductHandle
 * @property {() => HTMLFormElement | null} getForm
 * @property {() => number} getMinInstallments
 * @property {() => number} getSelectedQuantity
 * @property {() => number} getProductPriceCents
 * @property {() => { isAuthenticated: boolean, available: boolean, creditLimit: number, guestLimit: number }} getEligibilityConfig
 * @property {() => string} getSellingPlanInputId
 * @property {(gid: string | number) => void} [onSellingPlanApplied]
 * @property {ParentNode} [domRoot]
 */

/**
 * @param {BudgetPaySyncContext} ctx
 */
export async function computeTargetCountAndValidateFromContext(ctx) {
  const eligibility = ctx.getEligibilityConfig();
  if (!eligibility.available) {
    throw new Error('You have an outstanding Budget Pay balance. Please clear it to use Budget Pay again, or you can choose to pay in full.');
  }

  const newCount = ctx.getMinInstallments();
  const productHandle = ctx.getProductHandle() || '';
  const root = ctx.domRoot ?? document;

  if (!newCount || newCount < 1) {
    throw new Error('Invalid installment count for this product.');
  }

  const cart = await fetchCart();
  const bpLines = getBudgetPayCartLines(cart);
  const targetCount = getTargetInstallmentCount(bpLines, getMinInstallmentsFromLine, [newCount]);

  if (!targetCount) {
    throw new Error('Invalid installment count for this product.');
  }

  for (const line of bpLines) {
    if (!resolvePlanForLineItem(line, targetCount, root)) {
      throw new Error(
        `This product needs ${targetCount} installments, but another item in your cart cannot use that plan. Remove it or use a compatible product.`,
      );
    }
  }

  if (!getSellingPlanGid(targetCount, productHandle, root)) {
    if (bpLines.length === 0) {
      throw new Error('No selling plan is available for this product and installment count.');
    }
    throw new Error(
      `No ${targetCount}-installment selling plan is available for this product. It may not be in the same selling plan group as your cart.`,
    );
  }

  const selectedQty = ctx.getSelectedQuantity();
  const currentProductPriceCents = ctx.getProductPriceCents();
  const addedFullPriceCents = currentProductPriceCents * selectedQty;
  const exposure = computeBpExposureAtTargetCount(bpLines, targetCount, addedFullPriceCents);
  if (!exposure.ok) {
    throw new Error(exposure.message);
  }

  const validation = validateEligibility(eligibility, exposure.exposureCents);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  return { bpLines, targetCount, productHandle, cart };
}

/**
 * @param {BudgetPaySyncContext} ctx
 */
export async function syncBudgetPayStateWithContext(ctx) {
  const root = ctx.domRoot ?? document;
  mergeBpSellingPlansFromDomIntoBody();
  clearBudgetPayError(ctx.toggleEl);
  const form = ctx.getForm();
  ltvBpDebugLog('syncBudgetPayStateWithContext: start', {
    hasForm: !!form,
    formId: form?.id,
    formAction: form?.getAttribute('action'),
    productHandle: ctx.getProductHandle(),
    inputId: ctx.getSellingPlanInputId(),
    minInstallments: ctx.getMinInstallments(),
  });
  setProductFormSubmitDisabledForForm(form, true);
  try {
    const { targetCount, cart } = await computeTargetCountAndValidateFromContext(ctx);
    ltvBpDebugLog('syncBudgetPayStateWithContext: validated', {
      targetCount,
      bpLineCount: getBudgetPayCartLines(cart).length,
    });
    await alignCartBpLinesToTargetCount(cart, targetCount, root);
    const productHandle = ctx.getProductHandle() || '';
    const gid = applySellingPlanToForm(form, productHandle, targetCount, ctx.getSellingPlanInputId(), root);
    if (ctx.onSellingPlanApplied && gid != null) {
      ctx.onSellingPlanApplied(gid);
    }
    ltvBpDebugLog('syncBudgetPayStateWithContext: complete', { targetCount, gid });
    document.dispatchEvent(
      new CustomEvent('budget-pay:sync-complete', {
        bubbles: true,
        detail: { targetCount },
      }),
    );
  } finally {
    setProductFormSubmitDisabledForForm(form, false);
  }
}
